import React, { useState, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import {
  Container, Box, Typography, TextField, Button, Paper,
  Alert, CircularProgress, Link as MuiLink, FormControl,
  InputLabel, Select, MenuItem
} from '@mui/material';
import { Link } from 'react-router-dom';
import SocialSignIn from '../components/auth/SocialSignIn';
import { validateUsername, sanitizeUsername } from '../utils/usernameUtils';
import apiConfig from '../services/apiConfig';
import { gaEvent } from '../utils/analytics';

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Read ?role and ?returnTo from the URL (e.g. coming from a public influencer profile)
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const forcedRole = searchParams.get('role');   // 'brand' | null
  const returnTo   = searchParams.get('returnTo'); // encoded path | null

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    userType: forcedRole === 'brand' ? 'brand' : 'brand', // Default to brand; forced if param present
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');

  // Availability check states: null | 'checking' | 'available' | 'taken'
  const [usernameStatus, setUsernameStatus] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const usernameTimer = useRef(null);
  const emailTimer = useRef(null);

  const checkUsername = (value) => {
    if (value.length < 3) { setUsernameStatus(null); return; }
    setUsernameStatus('checking');
    clearTimeout(usernameTimer.current);
    usernameTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${apiConfig.core}/api/public/check-username?username=${encodeURIComponent(value)}`);
        const data = await res.json();
        setUsernameStatus(data.available ? 'available' : 'taken');
      } catch {
        setUsernameStatus(null);
      }
    }, 400);
  };

  const checkEmail = (value) => {
    if (!value || !value.includes('@')) { setEmailStatus(null); return; }
    setEmailStatus('checking');
    clearTimeout(emailTimer.current);
    emailTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${apiConfig.core}/api/public/check-email?email=${encodeURIComponent(value)}`);
        const data = await res.json();
        setEmailStatus(data.available ? 'available' : 'taken');
      } catch {
        setEmailStatus(null);
      }
    }, 400);
  };

  const handleChange = (e) => {
    let value = e.target.value;

    // Special handling for username field - make it communication-friendly
    if (e.target.name === 'username') {
      value = sanitizeUsername(value);
      checkUsername(value);
    }

    if (e.target.name === 'email') {
      checkEmail(value);
    }

    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords don't match");
      return;
    }

    // Validate username format for communication hub
    const usernameValidation = validateUsername(formData.username);
    if (!usernameValidation.isValid) {
      setError(usernameValidation.error);
      return;
    }

    if (usernameStatus === 'taken') {
      setError('That username is already taken. Please choose a different one.');
      return;
    }

    if (emailStatus === 'taken') {
      setError('An account with that email already exists. Please sign in instead.');
      return;
    }

    setLoading(true);
    
    try {
      // Sign up the user
      const { user } = await Auth.signUp({
        username: formData.username,
        password: formData.password,
        attributes: {
          email: formData.email,
          'custom:user_type': formData.userType,
          'custom:primaryRole': formData.userType,
          'custom:userTypes': JSON.stringify([formData.userType]),
        }
      });
      
      console.log('Sign up successful:', user);
      gaEvent('sign_up', { method: 'email', user_type: formData.userType });
      setVerificationSent(true);
    } catch (err) {
      console.error('Error signing up:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleVerification = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      await Auth.confirmSignUp(formData.username, verificationCode);
      // Redirect to login — carry returnTo so the brand lands back on the influencer profile
      const loginDest = returnTo
        ? `/login?returnTo=${encodeURIComponent(returnTo)}`
        : '/login';
      navigate(loginDest, { state: { message: 'Registration successful! Please sign in.' } });
    } catch (err) {
      console.error('Error confirming sign up:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            {verificationSent ? 'Verify Your Email' : 'Create an Account'}
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {!verificationSent ? (
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label="Username"
                name="username"
                autoComplete="username"
                value={formData.username}
                onChange={handleChange}
                placeholder="e.g., john_fitness_coach"
                error={usernameStatus === 'taken'}
                helperText={
                  usernameStatus === 'checking' ? '⏳ Checking availability…' :
                  usernameStatus === 'available' ? '✓ Username is available' :
                  usernameStatus === 'taken' ? '✗ Username is already taken' :
                  'Choose a unique username (e.g., john_fitness_coach). Others will find you as @john_fitness_coach'
                }
                FormHelperTextProps={{
                  sx: {
                    color: usernameStatus === 'available' ? 'success.main' :
                           usernameStatus === 'taken' ? 'error.main' : undefined,
                  }
                }}
                InputProps={{
                  startAdornment: <Box component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>@</Box>,
                }}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                error={emailStatus === 'taken'}
                helperText={
                  emailStatus === 'checking' ? '⏳ Checking…' :
                  emailStatus === 'available' ? '✓ Email is available' :
                  emailStatus === 'taken' ? '✗ An account with this email already exists' :
                  undefined
                }
                FormHelperTextProps={{
                  sx: {
                    color: emailStatus === 'available' ? 'success.main' :
                           emailStatus === 'taken' ? 'error.main' : undefined,
                  }
                }}
              />
              
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="new-password"
                value={formData.password}
                onChange={handleChange}
              />
              
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="Confirm Password"
                type="password"
                id="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
              />
              
              <FormControl fullWidth margin="normal">
                <InputLabel id="user-type-label">I am a</InputLabel>
                <Select
                  labelId="user-type-label"
                  id="userType"
                  name="userType"
                  value={formData.userType}
                  label="I am a"
                  onChange={handleChange}
                  disabled={forcedRole === 'brand'}
                >
                  <MenuItem value="brand">Brand</MenuItem>
                  <MenuItem value="influencer">Influencer</MenuItem>
                </Select>
                {forcedRole === 'brand' && (
                  <Box component="p" sx={{ fontSize: 12, color: '#6b7280', mt: 0.5, mb: 0 }}>
                    Register as a Brand to view and connect with influencer profiles.
                  </Box>
                )}
              </FormControl>
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading || usernameStatus === 'checking' || usernameStatus === 'taken' || emailStatus === 'checking' || emailStatus === 'taken'}
              >
                {loading ? <CircularProgress size={24} /> : 'Sign Up'}
              </Button>
              
              {/* Add social sign-in component */}
              <SocialSignIn />

              <Alert severity="info" sx={{ mt: 1.5, mb: 1, fontSize: '0.82rem' }}>
                Signing up with Google? After sign-in you'll be asked to select whether you're a <strong>Brand</strong> or <strong>Influencer</strong> before accessing your dashboard.
              </Alert>

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <MuiLink component={Link} to="/login" variant="body2">
                  Already have an account? Sign in
                </MuiLink>
              </Box>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleVerification} sx={{ mt: 1 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                We've sent a verification code to your email address. Please enter it below to verify your account.
              </Alert>
              
              <TextField
                margin="normal"
                required
                fullWidth
                id="verificationCode"
                label="Verification Code"
                name="verificationCode"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Verify'}
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default Register;
